// Projects (brief section 8.3), with the workspace's tasks and knowledge as tabs.
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useContextWorkspace } from '../components/Layout';
import { ProjectCard, TaskRow } from '../components/records';
import { Audience, Chip, Demo, Empty, ErrorBox, Field, Loading, Modal, PageHead, VisibilityPicker } from '../components/ui';
import { api, errorText } from '../lib/api';
import { day, words } from '../lib/format';
import { useSession } from '../lib/session';
import type { Knowledge, Member, Project, Task } from '../lib/types';
import { useApi } from '../lib/useApi';

const STAGES = ['incubator', 'exploring', 'active', 'paused', 'done', 'dropped'];
export const KINDS = ['sourced_fact', 'assumption', 'preference', 'decision', 'commitment', 'note'];

export function Projects() {
  const ws = useContextWorkspace()!;
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'projects';
  const open = params.get('open');
  const setTab = (t: string) => setParams(t === 'projects' ? {} : { tab: t });

  return (
    <>
      <PageHead title="Projects" sub={`Goals, tasks and what ${ws.name} knows.`} />
      <div className="tabs" role="tablist" aria-label="Projects, tasks and knowledge">
        {[
          ['projects', 'Projects'],
          ['tasks', 'Tasks'],
          ['knowledge', 'Knowledge'],
        ].map(([k, label]) => (
          <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>
            {label}
          </button>
        ))}
      </div>
      {tab === 'projects' && <ProjectList />}
      {tab === 'tasks' && <TaskList open={open} />}
      {tab === 'knowledge' && <KnowledgeList open={open} />}
    </>
  );
}

function ProjectList() {
  const ws = useContextWorkspace()!;
  const [stage, setStage] = useState('');
  const projects = useApi<Project[]>(`/api/w/${ws.slug}/projects${stage ? `?stage=${stage}` : ''}`);
  const [creating, setCreating] = useState(false);
  return (
    <>
      <div className="row between wrap gap toolbar">
        <label className="inline">
          Stage{' '}
          <select value={stage} onChange={(e) => setStage(e.target.value)}>
            <option value="">all</option>
            {STAGES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn primary" onClick={() => setCreating(true)}>
          New project
        </button>
      </div>
      {projects.error && <ErrorBox error={projects.error} retry={projects.reload} />}
      {!projects.data && !projects.error && <Loading />}
      {projects.data?.length === 0 && <Empty>No projects{stage ? ` at stage ${stage}` : ''} yet.</Empty>}
      <ul className="cards">
        {projects.data?.map((p) => (
          <ProjectCard key={p.id} p={p} slug={ws.slug} />
        ))}
      </ul>
      {creating && (
        <ProjectForm
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void projects.reload();
          }}
        />
      )}
    </>
  );
}

export function ProjectForm({ project, onClose, onSaved }: { project?: Project; onClose: () => void; onSaved: (p: Project) => void }) {
  const ws = useContextWorkspace()!;
  const { me } = useSession();
  const members = useApi<Member[]>(`/api/w/${ws.slug}/members`);
  const [form, setForm] = useState({
    title: project?.title ?? '',
    goal: project?.goal ?? '',
    audience: project?.audience ?? '',
    stage: project?.stage ?? 'incubator',
    owner_user_id: project?.owner?.id ?? me.user.id,
    capacity_hours_per_week: project?.capacity_hours_per_week?.toString() ?? '',
    visibility: (project?.visibility ?? 'workspace') as 'workspace' | 'personal',
  });
  const [error, setError] = useState('');
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm({ ...form, [k]: e.target.value });

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const body = {
      ...form,
      capacity_hours_per_week: form.capacity_hours_per_week === '' ? undefined : Number(form.capacity_hours_per_week),
      visibility: project && project.visibility === form.visibility ? undefined : form.visibility,
      owner_user_id: form.visibility === 'personal' ? undefined : form.owner_user_id,
    };
    try {
      const p = await api<Project>(project ? `/api/w/${ws.slug}/projects/${project.id}` : `/api/w/${ws.slug}/projects`, {
        method: project ? 'PATCH' : 'POST',
        body,
      });
      onSaved(p);
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <Modal title={project ? 'Edit project' : 'New project'} onClose={onClose} wide>
      <form onSubmit={save}>
        <Field label="Title">
          <input required value={form.title} onChange={set('title')} maxLength={200} />
        </Field>
        <Field label="Goal" hint="What does done look like?">
          <textarea rows={2} value={form.goal} onChange={set('goal')} />
        </Field>
        <Field label="Audience" hint="Who is it for? (client, customers, internal)">
          <input value={form.audience} onChange={set('audience')} />
        </Field>
        <div className="row gap wrap">
          <Field label="Stage">
            <select value={form.stage} onChange={set('stage')}>
              {STAGES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Accountable person">
            <select value={form.owner_user_id} onChange={set('owner_user_id')} disabled={form.visibility === 'personal'}>
              {members.data?.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Capacity (hours / week)">
            <input type="number" min={0} max={168} step={0.5} value={form.capacity_hours_per_week} onChange={set('capacity_hours_per_week')} />
          </Field>
        </div>
        {(!project || project.private) && (
          <VisibilityPicker value={form.visibility} onChange={(v) => setForm({ ...form, visibility: v })} workspaceName={ws.name} />
        )}
        {project && project.private && form.visibility === 'workspace' && (
          <p className="msg warn small">Publishing shares this draft with everyone in {ws.name}. It cannot be made private again.</p>
        )}
        {error && <p className="msg err">{error}</p>}
        <footer className="modal-foot">
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn primary">
            Save
          </button>
        </footer>
      </form>
    </Modal>
  );
}

function TaskList({ open }: { open: string | null }) {
  const ws = useContextWorkspace()!;
  const [filter, setFilter] = useState('open');
  const states = filter === 'open' ? 'state=proposed&state=ready&state=running&state=waiting_for_input&state=waiting_for_approval&state=failed' : filter === 'done' ? 'state=completed&state=cancelled' : '';
  const tasks = useApi<Task[]>(`/api/w/${ws.slug}/tasks${states ? `?${states}` : ''}`);
  const [creating, setCreating] = useState(false);
  useEffect(() => {
    if (open && tasks.data) document.getElementById(`task-${open}`)?.scrollIntoView({ block: 'center' });
  }, [open, tasks.data]);
  return (
    <>
      <div className="row between wrap gap toolbar">
        <label className="inline">
          Show{' '}
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="open">open tasks</option>
            <option value="done">completed and cancelled</option>
            <option value="all">all</option>
          </select>
        </label>
        <button type="button" className="btn primary" onClick={() => setCreating(true)}>
          New task
        </button>
      </div>
      {tasks.error && <ErrorBox error={tasks.error} retry={tasks.reload} />}
      {!tasks.data && !tasks.error && <Loading />}
      {tasks.data?.length === 0 && <Empty>No tasks here.</Empty>}
      <ul className="recs">
        {tasks.data?.map((t) => (
          <TaskRow key={t.id} task={t} slug={ws.slug} onChanged={() => void tasks.reload()} highlight={t.id === open} />
        ))}
      </ul>
      {creating && (
        <TaskForm
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void tasks.reload();
          }}
        />
      )}
    </>
  );
}

export function TaskForm({ projectId, onClose, onSaved }: { projectId?: string; onClose: () => void; onSaved: (t: Task) => void }) {
  const ws = useContextWorkspace()!;
  const members = useApi<Member[]>(`/api/w/${ws.slug}/members`);
  const [form, setForm] = useState({ title: '', description: '', owner_user_id: '', due_date: '', estimate_hours: '' });
  const [vis, setVis] = useState<'workspace' | 'personal'>('workspace');
  const [error, setError] = useState('');
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm({ ...form, [k]: e.target.value });

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      const t = await api<Task>(`/api/w/${ws.slug}/tasks`, {
        body: {
          title: form.title,
          description: form.description || undefined,
          owner_user_id: vis === 'workspace' && form.owner_user_id ? form.owner_user_id : undefined,
          due_date: form.due_date || undefined,
          estimate_hours: form.estimate_hours ? Number(form.estimate_hours) : undefined,
          project_id: projectId,
          visibility: vis,
        },
      });
      onSaved(t);
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <Modal title="New task" onClose={onClose}>
      <form onSubmit={save}>
        <Field label="Title">
          <input required value={form.title} onChange={set('title')} maxLength={300} />
        </Field>
        <Field label="Details">
          <textarea rows={2} value={form.description} onChange={set('description')} />
        </Field>
        <div className="row gap wrap">
          <Field label="Accountable person">
            <select value={form.owner_user_id} onChange={set('owner_user_id')} disabled={vis === 'personal'}>
              <option value="">not decided</option>
              {members.data?.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Due">
            <input type="date" value={form.due_date} onChange={set('due_date')} />
          </Field>
          <Field label="Estimate (hours)">
            <input type="number" min={0} step={0.5} value={form.estimate_hours} onChange={set('estimate_hours')} />
          </Field>
        </div>
        <VisibilityPicker value={vis} onChange={setVis} workspaceName={ws.name} />
        <p className="muted small">New tasks start as proposed; move them to ready when someone commits to them.</p>
        {error && <p className="msg err">{error}</p>}
        <footer className="modal-foot">
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn primary">
            Add task
          </button>
        </footer>
      </form>
    </Modal>
  );
}

function KnowledgeList({ open }: { open: string | null }) {
  const ws = useContextWorkspace()!;
  const [kind, setKind] = useState('');
  const items = useApi<Knowledge[]>(`/api/w/${ws.slug}/knowledge${kind ? `?kind=${kind}` : ''}`);
  const [creating, setCreating] = useState(false);
  return (
    <>
      <div className="row between wrap gap toolbar">
        <label className="inline">
          Type{' '}
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">all</option>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {words(k)}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn primary" onClick={() => setCreating(true)}>
          Add knowledge
        </button>
      </div>
      {items.error && <ErrorBox error={items.error} retry={items.reload} />}
      {!items.data && !items.error && <Loading />}
      {items.data?.length === 0 && <Empty>Nothing recorded{kind ? ` of type ${words(kind)}` : ''} yet.</Empty>}
      <ul className="recs">
        {items.data?.map((k) => (
          <KnowledgeRow key={k.id} k={k} highlight={k.id === open} />
        ))}
      </ul>
      {creating && (
        <KnowledgeForm
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void items.reload();
          }}
        />
      )}
    </>
  );
}

export function KnowledgeRow({ k, highlight }: { k: Knowledge; highlight?: boolean }) {
  return (
    <li className={`rec knowledge${highlight ? ' highlight' : ''}`} id={`knowledge-${k.id}`}>
      <div className="rec-main">
        <span className="rec-title">{k.title}</span>
        <div className="rec-meta">
          <Chip value={k.kind} />
          <Audience item={k} />
          <Demo show={k.demo} />
          {k.source_label && <span className="muted small">source: {k.source_label}</span>}
          <span className="muted small">{day(k.created_at)}</span>
        </div>
        {k.body && <p className="small pre">{k.body}</p>}
      </div>
    </li>
  );
}

export function KnowledgeForm({ projectId, onClose, onSaved }: { projectId?: string; onClose: () => void; onSaved: (k: Knowledge) => void }) {
  const ws = useContextWorkspace()!;
  const [form, setForm] = useState({ kind: 'note', title: '', body: '', source_label: '' });
  const [vis, setVis] = useState<'workspace' | 'personal'>('workspace');
  const [error, setError] = useState('');
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm({ ...form, [k]: e.target.value });

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      onSaved(
        await api<Knowledge>(`/api/w/${ws.slug}/knowledge`, {
          body: { ...form, source_label: form.source_label || undefined, project_id: projectId, visibility: vis },
        }),
      );
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <Modal title="Add knowledge" onClose={onClose} wide>
      <form onSubmit={save}>
        <div className="row gap wrap">
          <Field label="Type" hint="A sourced fact needs a source; an assumption is not yet checked.">
            <select value={form.kind} onChange={set('kind')}>
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {words(k)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Source">
            <input value={form.source_label} onChange={set('source_label')} placeholder="e.g. client e-mail of 14 Oct" required={form.kind === 'sourced_fact'} />
          </Field>
        </div>
        <Field label="Title">
          <input required value={form.title} onChange={set('title')} maxLength={300} />
        </Field>
        <Field label="Details">
          <textarea rows={5} value={form.body} onChange={set('body')} />
        </Field>
        <VisibilityPicker value={vis} onChange={setVis} workspaceName={ws.name} />
        {error && <p className="msg err">{error}</p>}
        <footer className="modal-foot">
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn primary">
            Save
          </button>
        </footer>
      </form>
    </Modal>
  );
}
